import { Component, EventEmitter, Injector, Input, OnInit, Output } from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { LocalStorageManager } from '@cutting/main/shared/helper/local-storage-manager.helper';
import {
  BazisMaterialHelper,
  CuttingMaterial,
  CuttingMaterialBaseDialogComponent,
  CuttingMaterialBaseDialogData,
  CuttingSettings,
  DialogsService,
  MaterialForReplacementToElement,
  PriceListProductBazisElement,
  PriceListProductElementEdge,
  ProductProcessingMode,
} from 'cloud-shared-lib';
import * as _ from 'lodash';
import { filter, finalize, map } from 'rxjs/operators';

import { NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { OrderDetailService } from '@cutting/main/order/order-detail/order-detail.service';
import { ProductElementEdgeDialogComponent } from './product-element-edge-dialog/product-element-edge-dialog.component';
import {
  RecommendedMaterialsDialogComponent,
  RecommendedMaterialsDialogData,
} from './recomended-materials-dialog/recomended-materials-dialog.component';

@Component({
  selector: 'cutting-product-elements',
  templateUrl: './product-elements.component.html',
  styleUrls: ['./product-elements.component.scss'],
  standalone: true,
  imports: [NgIf, MatIconModule, MatTableModule, MatButtonModule],
})
export class ProductElementsComponent implements OnInit {
  @Input() priceListId = 0;
  @Input() multiplier = 1;
  @Input() cuttingOptions: CuttingSettings;
  @Input() productId: number;
  @Input() processingMode: ProductProcessingMode;

  @Output() elementsChanged: EventEmitter<PriceListProductBazisElement[]> = new EventEmitter();
  @Output() elementMaterialChanged: EventEmitter<[PriceListProductBazisElement, MaterialForReplacementToElement]> =
    new EventEmitter();

  displayedColumns: string[] = ['article', 'name', 'materialName', 'changeMaterial', 'edge', 'count', 'delete'];
  dataSource: MatTableDataSource<PriceListProductBazisElement> = new MatTableDataSource([]);
  materialBaseDialogRef: MatDialogRef<CuttingMaterialBaseDialogComponent>;
  edgeDialogRef: MatDialogRef<ProductElementEdgeDialogComponent>;

  originalElementList: PriceListProductBazisElement[] = [];

  @Input() set elements(val: PriceListProductBazisElement[]) {
    this.originalElementList = _.cloneDeep(val);
    this.dataSource.data = this.originalElementList.filter(e => e.isVisible);
    if (!this.hasArticles(val)) {
      this.displayedColumns.shift();
    }
  }

  constructor(public dialog: MatDialog, private dialogsService: DialogsService, private injector: Injector) {}

  ngOnInit(): void {
    this.settingUpDisplayedColumns();
  }

  get userId(): number {
    return LocalStorageManager.regData;
  }

  hasArticles(val: PriceListProductBazisElement[]): boolean {
    return !!val.reduce((acc, e) => acc + e, '').trim();
  }

  settingUpDisplayedColumns(): void {
    if (this.cuttingOptions.cuttingProductSettings.disableDeletingProductElements) {
      const index = this.displayedColumns.findIndex(c => c === 'delete');
      if (index !== -1) {
        this.displayedColumns.splice(index, 1);
      }
    }
  }

  openRecomendedMaterialsDialog(element: PriceListProductBazisElement): void {
    const parentInjector = this.injector;

    this.dialogsService
      .component<RecommendedMaterialsDialogData>(
        RecommendedMaterialsDialogComponent,
        {
          elementId: element.id,
          productId: this.productId,
          cuttingOptions: this.cuttingOptions,
          priceListId: this.priceListId,
        },
        700,
        {
          panelClass: 'price-list-recomended-materials-dialog',
          injector: Injector.create({
            providers: [{ provide: OrderDetailService, useValue: parentInjector.get(OrderDetailService) }],
            parent: parentInjector,
          }),
        }
      )
      .pipe(filter(m => m && !!m?.length))
      .subscribe(m => {
        this.changeElementMaterial(element, m[0]);
      });
  }

  openMaterialBaseDialog(element: PriceListProductBazisElement): void {
    this.materialBaseDialogRef = this.dialog.open(CuttingMaterialBaseDialogComponent, {
      panelClass: 'material-base-material-list-dialog',
      disableClose: false,
      width: '1150px',
      data: {
        materialsToExclude: [],
        multiple: false,
        showQuantityInStock: this.cuttingOptions.showQuantityInStock,
      } as CuttingMaterialBaseDialogData,
    });

    this.materialBaseDialogRef
      .afterClosed()
      .pipe(
        filter(result => result && result.length > 0),
        map(result => result[0]),
        finalize(() => (this.materialBaseDialogRef = null))
      )
      .subscribe((result: CuttingMaterial) => {
        const material = new MaterialForReplacementToElement();
        material.materialName = BazisMaterialHelper.increaseMaterial(result.name, result.article);
        material.textureName = result.textureName;
        // material.article = result.article;
        material.materialId = result.id;
        material.price = 0;

        this.changeElementMaterial(element, material);
      });
  }

  formatMaterialName(materialName: string): string {
    return BazisMaterialHelper.formatMaterialName(materialName);
  }

  deleteElement(item: PriceListProductBazisElement): void {
    const index = this.originalElementList.findIndex(e => e.id === item.id);

    if (index !== -1) {
      this.originalElementList.splice(index, 1);
      this.dataSource.data = this.originalElementList;

      this.elementsChanged.next(this.originalElementList);
    }
  }

  openEdgeDialog(element: PriceListProductBazisElement): void {
    const parentInjector = this.injector;

    this.edgeDialogRef = this.dialog.open(ProductElementEdgeDialogComponent, {
      panelClass: 'product-element-edge-dialog',
      disableClose: false,
      width: '1000px',
      injector: Injector.create({
        providers: [{ provide: OrderDetailService, useValue: parentInjector.get(OrderDetailService) }],
        parent: parentInjector,
      }),
    });

    this.edgeDialogRef.componentInstance.edges = element.edgeList;
    this.edgeDialogRef.componentInstance.materialName = element.materialName;
    this.edgeDialogRef.componentInstance.cuttingOptions = this.cuttingOptions;

    this.edgeDialogRef
      .afterClosed()
      .pipe(
        filter(result => !!result),
        finalize(() => (this.edgeDialogRef = null))
      )
      .subscribe((result: PriceListProductElementEdge[]) => {
        element.edgeList = result;
        this.elementsChanged.emit(this.originalElementList);
      });
  }

  private changeElementMaterial(
    element: PriceListProductBazisElement,
    newMaterial: MaterialForReplacementToElement
  ): void {
    element.materialId = newMaterial.materialId;
    element.materialName = newMaterial.materialName;
    element.price = newMaterial.price;
    element.edgeList?.forEach(e => {
      e.name = '';
      e.thickness = 0;
    });

    this.elementMaterialChanged.emit([element, newMaterial]);
    this.elementsChanged.emit(this.originalElementList);

    if (element.edgeList?.length) {
      this.openEdgeDialog(element);
    }
  }
}
